import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Link2, Store } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  spApiOauthApi,
  type SpApiConnectionType,
  type SpApiOauthConnection,
  type SpApiRegion,
  type SpApiRegionsResponse,
  type SpApiVendorLocation,
} from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';

export const SP_API_OAUTH_CONNECTIONS_QUERY_KEY = ['sp-api-oauth-connections'] as const;
export const SP_API_REGIONS_QUERY_KEY = ['sp-api-regions'] as const;

async function startSpApiOAuth(
  type: SpApiConnectionType,
  credentialId?: string | null,
  opts?: { region?: string; location?: string }
) {
  const res = credentialId
    ? await spApiOauthApi.reconnect(credentialId)
    : await spApiOauthApi.getAuthorizeUrl({
        type,
        region: opts?.region,
        location: opts?.location,
      });
  if (!res.success || !res.data?.authorize_url) {
    throw new Error('Could not start Seller/Vendor authorization');
  }
  window.location.href = res.data.authorize_url;
}

export function useSpApiOauthConnections(enabled = true) {
  return useQuery({
    queryKey: SP_API_OAUTH_CONNECTIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await spApiOauthApi.listConnections();
      if (!res.success) throw new Error('Failed to load Seller/Vendor connections');
      return res.data;
    },
    enabled,
    refetchInterval: 5 * 60_000,
  });
}

function useSpApiRegionsMeta() {
  return useQuery({
    queryKey: SP_API_REGIONS_QUERY_KEY,
    queryFn: async (): Promise<SpApiRegionsResponse> => {
      const res = await spApiOauthApi.listRegions();
      if (!res.success) throw new Error('Failed to load regions');
      return res.data;
    },
    staleTime: 60 * 60_000,
  });
}

export function useConnectSpApiMutation() {
  return useMutation({
    mutationFn: ({
      type,
      region,
      location,
    }: {
      type: SpApiConnectionType;
      region?: string;
      location?: string;
    }) => startSpApiOAuth(type, null, { region, location }),
  });
}

export function useReconnectSpApiMutation() {
  return useMutation({
    mutationFn: (credentialId: string) => startSpApiOAuth('seller', credentialId),
  });
}

function healthLabel(health: string): string {
  switch (health) {
    case 'reauth_required':
      return 'Re-authorization required';
    case 'expiring_soon':
      return 'Expiring soon';
    case 'inactive_risk':
      return 'At risk (inactive)';
    default:
      return 'Active';
  }
}

function typeLabel(type: string): string {
  return type === 'vendor' ? 'Vendor Central' : 'Seller Central';
}

function authSourceLabel(source: string | undefined): string {
  return source === 'self_authorized' ? 'Self-authorized' : 'OAuth';
}

function axiosErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      return String((detail as { message?: string }).message || error.message);
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

function RegionSelect({
  id,
  regions,
  value,
  onChange,
  disabled,
}: {
  id: string;
  regions: SpApiRegion[];
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
}) {
  return (
    <select
      id={id}
      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {regions.map((r) => (
        <option key={r.code} value={r.code}>
          {r.label} ({r.code})
        </option>
      ))}
    </select>
  );
}

function VendorLocationSelect({
  id,
  locations,
  value,
  onChange,
  disabled,
}: {
  id: string;
  locations: SpApiVendorLocation[];
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
}) {
  return (
    <select
      id={id}
      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {locations.map((loc) => (
        <option key={loc.code} value={loc.code}>
          {loc.label} ({loc.code})
        </option>
      ))}
    </select>
  );
}

function MarketplaceSelect({
  id,
  regions,
  regionCode,
  value,
  onChange,
  disabled,
}: {
  id: string;
  regions: SpApiRegion[];
  regionCode: string;
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}) {
  const marketplaces = useMemo(() => {
    const region = regions.find((r) => r.code === regionCode);
    return region?.marketplaces ?? [];
  }, [regions, regionCode]);

  useEffect(() => {
    if (!marketplaces.length) return;
    if (!marketplaces.some((m) => m.id === value)) {
      onChange(marketplaces[0].id);
    }
  }, [marketplaces, value, onChange]);

  return (
    <select
      id={id}
      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
      value={value}
      disabled={disabled || marketplaces.length === 0}
      onChange={(e) => onChange(e.target.value)}
    >
      {marketplaces.map((m) => (
        <option key={m.id} value={m.id}>
          {m.label} ({m.id})
        </option>
      ))}
    </select>
  );
}

export function SpApiConnectionsPanel() {
  const { data, isLoading, isError, refetch } = useSpApiOauthConnections();
  const { data: regionsMeta, isLoading: regionsLoading } = useSpApiRegionsMeta();
  const regions = regionsMeta?.regions ?? [];
  const vendorLocations = regionsMeta?.vendor_locations ?? [];
  const connect = useConnectSpApiMutation();
  const reconnect = useReconnectSpApiMutation();
  const qc = useQueryClient();
  const { showToast } = useToast();

  const defaultRegion =
    regionsMeta?.default_region ||
    regions.find((r) => r.code === 'IN')?.code ||
    regions[0]?.code ||
    'IN';
  const defaultVendorLocation =
    regionsMeta?.default_vendor_location ||
    vendorLocations.find((l) => l.code === 'IN')?.code ||
    vendorLocations[0]?.code ||
    'IN';
  const [vendorLocation, setVendorLocation] = useState(defaultVendorLocation);
  const [sellerRegion, setSellerRegion] = useState(defaultRegion);
  const [showSelfAuth, setShowSelfAuth] = useState(false);
  const [selfRegion, setSelfRegion] = useState(defaultRegion);
  const [selfMarketplace, setSelfMarketplace] = useState('');
  const [selfToken, setSelfToken] = useState('');
  const [selfSpId, setSelfSpId] = useState('');
  const [selfDisplayName, setSelfDisplayName] = useState('');
  const [editingCredentialId, setEditingCredentialId] = useState<string | null>(null);

  useEffect(() => {
    if (!regions.length && !vendorLocations.length) return;
    const regionFallback =
      regionsMeta?.default_region ||
      regions.find((r) => r.code === 'IN')?.code ||
      regions[0]?.code ||
      'IN';
    const locationFallback =
      regionsMeta?.default_vendor_location ||
      vendorLocations.find((l) => l.code === 'IN')?.code ||
      vendorLocations[0]?.code ||
      'IN';
    if (regions.length && !regions.some((r) => r.code === sellerRegion)) {
      setSellerRegion(regionFallback);
    }
    if (regions.length && !regions.some((r) => r.code === selfRegion)) {
      setSelfRegion(regionFallback);
    }
    if (
      vendorLocations.length &&
      !vendorLocations.some((l) => l.code === vendorLocation)
    ) {
      setVendorLocation(locationFallback);
    }
  }, [
    regions,
    vendorLocations,
    regionsMeta,
    sellerRegion,
    selfRegion,
    vendorLocation,
  ]);

  const selfAuthMutation = useMutation({
    mutationFn: () =>
      spApiOauthApi.selfAuthorize({
        refresh_token: selfToken.trim(),
        region: selfRegion,
        marketplace_id: selfMarketplace,
        selling_partner_id: selfSpId.trim() || null,
        display_name: selfDisplayName.trim() || null,
        credential_id: editingCredentialId,
      }),
    onSuccess: (res) => {
      if (!res.success) throw new Error('Failed to save Vendor connection');
      showToast(
        editingCredentialId ? 'Vendor self-auth token updated' : 'Vendor self-auth connected',
        'success'
      );
      setSelfToken('');
      setSelfSpId('');
      setSelfDisplayName('');
      setEditingCredentialId(null);
      setShowSelfAuth(false);
      void qc.invalidateQueries({ queryKey: SP_API_OAUTH_CONNECTIONS_QUERY_KEY });
    },
    onError: (e: unknown) => {
      showToast(axiosErrorMessage(e), 'error');
    },
  });

  function openSelfAuthEdit(c: SpApiOauthConnection) {
    setEditingCredentialId(c.id);
    setSelfRegion(c.region || defaultRegion);
    setSelfMarketplace(c.marketplace_id || '');
    setSelfSpId(
      c.selling_partner_id?.startsWith('selfauth:') ? '' : c.selling_partner_id || ''
    );
    setSelfDisplayName(c.display_name || '');
    setSelfToken('');
    setShowSelfAuth(true);
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Seller & Vendor Central</h2>
        <p className="text-body-sm text-foreground-muted mt-1">
          Connect via Website OAuth, or paste a Vendor Central self-authorized refresh token.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
        <p className="text-sm font-medium text-gray-900">Website OAuth</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="seller-oauth-region" className="block text-xs text-gray-500 mb-1">
              Seller region
            </label>
            {regionsLoading ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <RegionSelect
                id="seller-oauth-region"
                regions={regions}
                value={sellerRegion}
                onChange={setSellerRegion}
              />
            )}
            <Button
              type="button"
              variant="secondary"
              className="mt-2 w-full sm:w-auto"
              onClick={() => connect.mutate({ type: 'seller', region: sellerRegion })}
              disabled={connect.isPending || regionsLoading}
            >
              {connect.isPending && connect.variables?.type === 'seller' ? (
                <>
                  <Spinner className="h-3.5 w-3.5 mr-1.5" />
                  Redirecting…
                </>
              ) : (
                <>
                  <Store className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                  Connect Seller
                </>
              )}
            </Button>
          </div>
          <div>
            <label htmlFor="vendor-oauth-location" className="block text-xs text-gray-500 mb-1">
              Vendor Central location
            </label>
            {regionsLoading ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <VendorLocationSelect
                id="vendor-oauth-location"
                locations={vendorLocations}
                value={vendorLocation}
                onChange={setVendorLocation}
              />
            )}
            <p className="mt-1 text-xs text-gray-500">
              Defaults to India. Pick the store where the vendor signs in.
            </p>
            <Button
              type="button"
              className="mt-2 w-full sm:w-auto"
              onClick={() => connect.mutate({ type: 'vendor', location: vendorLocation })}
              disabled={connect.isPending || regionsLoading || !vendorLocation}
            >
              {connect.isPending && connect.variables?.type === 'vendor' ? (
                <>
                  <Spinner className="h-3.5 w-3.5 mr-1.5" />
                  Redirecting…
                </>
              ) : (
                <>
                  <Link2 className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                  Connect Vendor (OAuth)
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Vendor self-authorization</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Paste a refresh token from Vendor Central Developer Central (self-authorize).
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setEditingCredentialId(null);
              setShowSelfAuth((v) => !v);
            }}
          >
            <KeyRound className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            {showSelfAuth ? 'Hide' : 'Add token'}
          </Button>
        </div>

        {showSelfAuth && (
          <form
            className="space-y-3 border-t border-gray-100 pt-3"
            onSubmit={(e) => {
              e.preventDefault();
              selfAuthMutation.mutate();
            }}
          >
            {editingCredentialId ? (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
                Updating existing self-authorized connection. Paste a new refresh token below.
              </p>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="self-region" className="block text-xs text-gray-500 mb-1">
                  Region
                </label>
                <RegionSelect
                  id="self-region"
                  regions={regions}
                  value={selfRegion}
                  onChange={setSelfRegion}
                  disabled={regionsLoading}
                />
              </div>
              <div>
                <label htmlFor="self-marketplace" className="block text-xs text-gray-500 mb-1">
                  Marketplace
                </label>
                <MarketplaceSelect
                  id="self-marketplace"
                  regions={regions}
                  regionCode={selfRegion}
                  value={selfMarketplace}
                  onChange={setSelfMarketplace}
                  disabled={regionsLoading}
                />
              </div>
            </div>
            <div>
              <label htmlFor="self-token" className="block text-xs text-gray-500 mb-1">
                Refresh token
              </label>
              <textarea
                id="self-token"
                required
                rows={3}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-mono text-gray-900"
                placeholder="Atzr|…"
                value={selfToken}
                onChange={(e) => setSelfToken(e.target.value)}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="self-sp-id" className="block text-xs text-gray-500 mb-1">
                  Selling partner ID (optional)
                </label>
                <Input
                  id="self-sp-id"
                  value={selfSpId}
                  onChange={(e) => setSelfSpId(e.target.value)}
                  placeholder="Leave blank to auto-generate"
                />
              </div>
              <div>
                <label htmlFor="self-display" className="block text-xs text-gray-500 mb-1">
                  Display name (optional)
                </label>
                <Input
                  id="self-display"
                  value={selfDisplayName}
                  onChange={(e) => setSelfDisplayName(e.target.value)}
                  placeholder="e.g. India Vendor"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={selfAuthMutation.isPending || !selfToken.trim()}>
                {selfAuthMutation.isPending ? (
                  <>
                    <Spinner className="h-3.5 w-3.5 mr-1.5" />
                    Validating…
                  </>
                ) : editingCredentialId ? (
                  'Update token'
                ) : (
                  'Save Vendor connection'
                )}
              </Button>
              {editingCredentialId ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setEditingCredentialId(null);
                    setSelfToken('');
                    setShowSelfAuth(false);
                  }}
                >
                  Cancel
                </Button>
              ) : null}
            </div>
          </form>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Could not load connections.{' '}
          <button type="button" className="underline font-medium" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      ) : (data?.connections?.length ?? 0) === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-gray-600 text-sm">
          No Seller or Vendor Central accounts connected yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {(data?.connections ?? []).map((c: SpApiOauthConnection) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3"
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {c.display_name || typeLabel(c.connection_type)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 mr-1 text-[10px] font-medium uppercase tracking-wide text-gray-700">
                    {authSourceLabel(c.auth_source)}
                  </span>
                  {typeLabel(c.connection_type)} · {c.region}
                  {c.marketplace_id ? ` · ${c.marketplace_id}` : ''} · {healthLabel(c.health)}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to={`/admin/retail/${encodeURIComponent(c.id)}`}
                  className="text-sm font-medium text-brand hover:underline"
                >
                  View data
                </Link>
                {c.auth_source === 'self_authorized' ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => openSelfAuthEdit(c)}
                  >
                    Update token
                  </Button>
                ) : (
                  (c.health === 'reauth_required' ||
                    c.health === 'expiring_soon' ||
                    c.health === 'inactive_risk') && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={reconnect.isPending}
                      onClick={() =>
                        reconnect.mutate(c.id, {
                          onSettled: () =>
                            qc.invalidateQueries({ queryKey: SP_API_OAUTH_CONNECTIONS_QUERY_KEY }),
                        })
                      }
                    >
                      Reconnect
                    </Button>
                  )
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
